import React, { Children } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Phone, ChevronDown } from 'lucide-react';
export function HeroSection() {
  const {
    scrollY
  } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 200]);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  const stats = [{
    number: '15+',
    label: 'Years Experience'
  }, {
    number: '1,000+',
    label: 'Projects Completed'
  }, {
    number: '5-Star',
    label: 'Google Rating'
  }];
  return <section className="relative min-h-[700px] md:min-h-[85vh] flex items-center pt-20 overflow-hidden pb-24 md:pb-0" data-id="element-287">
      {/* Background Image */}
      <motion.div style={{
      y: bgY
    }} className="absolute inset-0 z-0" data-id="element-288">
        <img src="https://images.unsplash.com/photo-1449844908441-8829872d2607?w=1920&q=80" alt="" className="absolute inset-0 w-full h-[120%] object-cover" data-id="element-289" />
        {/* Blue brand tint */}
        <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-290"></div>
        {/* Dark gradient overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20 z-[2]" data-id="element-291"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent z-[2]" data-id="element-292"></div>
      </motion.div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-12 md:mt-0" data-id="element-293">
        <div className="max-w-4xl" data-id="element-294">
          <motion.div variants={containerVariants} initial="hidden" animate="visible" data-id="element-295">
            <motion.div variants={itemVariants} data-id="element-296">
              <span className="inline-block py-1.5 px-4 rounded-full bg-brand-blue/30 border border-brand-blue text-white font-body text-sm font-bold mb-8 backdrop-blur-md shadow-[0_0_15px_rgba(1,181,255,0.3)] tracking-wide uppercase" data-id="element-297">
                Serving St. Clair, Macomb & Oakland Counties
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-heading font-black text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-4 drop-shadow-2xl" data-id="element-298">
              Quality That's{' '}
              <span className="text-brand-blue" data-id="element-299">Through The Roof</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="font-heading font-bold text-xl md:text-2xl text-white/90 mb-4 drop-shadow-lg" data-id="element-300">
              Macomb & St. Clair County's Most Trusted Exterior Contractor
            </motion.p>

            <motion.p variants={itemVariants} className="font-body text-lg md:text-xl text-gray-300 mb-10 max-w-2xl leading-relaxed drop-shadow-lg" data-id="element-301">
              At Moceri Residential Services, we deliver more than just a
              service — we deliver peace of mind. Upfront pricing, on-time
              crews, and 15+ years of Michigan craftsmanship.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-5" data-id="element-302">
              <a href="#quote" className="bg-brand-blue hover:bg-brand-blue-dark text-white font-body font-bold text-lg py-4 px-10 rounded-lg text-center transition-all duration-300 shadow-[0_0_20px_rgba(1,181,255,0.4)] hover:shadow-[0_0_30px_rgba(1,181,255,0.6)] hover:-translate-y-1" data-id="element-303">
                Get a Free Quote
              </a>
              <a href="tel:5862033701" className="bg-white/10 hover:bg-white text-white hover:text-brand-blue border-2 border-white backdrop-blur-sm font-body font-bold text-lg py-4 px-10 rounded-lg text-center transition-all duration-300 shadow-lg flex items-center justify-center gap-3 hover:-translate-y-1 group" data-id="element-304">
                <Phone size={22} className="group-hover:animate-pulse" data-id="element-305" />
                Call (586) 203-3701
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Floating Stats Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-30 translate-y-1/2 hidden md:block" data-id="element-306">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-307">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1,
          duration: 0.8
        }} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 grid grid-cols-3 gap-8 divide-x divide-gray-100 border border-gray-100" data-id="element-308">
            {stats.map((stat, index) => <div key={index} className="text-center px-4" data-id="element-309">
                <div className="font-heading font-black text-4xl lg:text-5xl text-brand-blue mb-2" data-id="element-310">
                  {stat.number}
                </div>
                <div className="font-body font-bold text-brand-gray-dark uppercase tracking-wider text-sm" data-id="element-311">
                  {stat.label}
                </div>
              </div>)}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator — mobile only */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center text-white/70 md:hidden" data-id="element-312">
        <span className="font-body text-xs uppercase tracking-widest mb-2 font-semibold" data-id="element-313">
          Scroll to explore
        </span>
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'easeInOut'
      }} data-id="element-314">
          <ChevronDown size={24} data-id="element-315" />
        </motion.div>
      </motion.div>
    </section>;
}